import type { RequestContext } from "../context/request-context.js";
import type { TechnicalReviewQueueRow, TechnicalReviewService } from "./technical-review-service.js";
import type { TechnicalCorrectionService } from "./technical-correction-service.js";
import type { DocumentHoldService } from "./document-hold-service.js";
import type { MinutaService } from "./minuta-service.js";
import type { TechnicalDocumentReview, TechnicalDocumentType } from "../../domain/review/technical-document-review.js";
import { TECHNICAL_DOCUMENT_TYPE_LABELS } from "../../domain/review/technical-document-review.js";
import {
  mergeEngineeringActivity,
  summarizeCorrections,
  type CorrectionSummary,
  type EngineeringActivityEntry
} from "../../domain/review/engineering-activity.js";
import {
  ENGINEERING_WORKFLOW_STATUS_LABELS,
  workflowStatusOfMinuta,
  workflowStatusOfReview,
  type EngineeringWorkflowStatus
} from "../../domain/review/engineering-workflow-status.js";
import { priorityWeight, relativeTimeLabel, type TechnicalCorrectionPriority, type TechnicalCorrectionRequest } from "../../domain/review/technical-correction.js";
import type { DocumentHold } from "../../domain/review/document-hold.js";
import type { Minuta } from "../../domain/report/minuta-domain.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

/**
 * PAINEL DE ENGENHARIA — central de gestão técnica. Agrega, por leitura, o
 * que cada mecanismo já tem (revisão PLH central + RI, correções por item,
 * HOLD, Minutas) numa fila única priorizada, com cards que funcionam como
 * filtro e a visão "MINHAS PENDÊNCIAS". Nada aqui muda estado.
 */

export interface EngineeringQueueRow extends TechnicalReviewQueueRow {
  workflowStatus: EngineeringWorkflowStatus;
  workflowLabel: string;
  /** Maior prioridade entre as correções pendentes do documento (nula sem pendência). */
  priority: TechnicalCorrectionPriority | null;
  pendingCorrections: number;
  /** "Há 2 horas", "Há 1 dia"… desde o envio (ou reenvio). */
  waitingLabel: string;
  waitingMs: number;
  onHold: boolean;
  holdId: string | null;
  /** É o PLH da sessão quem deve agir? */
  mine: boolean;
}

export interface EngineeringCounts {
  awaitingAnalysis: number;
  inAnalysis: number;
  /** AVRs do contexto (qualquer estado) — fila "AVR PARA ANÁLISE". */
  avrForAnalysis: number;
  /** Itens de correção por item ainda abertos. */
  pendingCorrections: number;
  awaitingCorrection: number;
  resubmitted: number;
  onHold: number;
  /** RIs validados cuja Minuta Técnica ainda não foi validada. */
  minutaPending: number;
  minutasInProgress: number;
  awaitingApproval: number;
  approved: number;
  /** Aprovados pelo PLH ainda não finalizados. */
  awaitingFinalization: number;
  finalized: number;
}

export interface MyPendencies {
  awaitingMyAnalysis: number;
  resubmittedToMe: number;
  onHold: number;
  minutasPending: number;
  correctionsRequestedByMe: number;
}

export type EngineeringCardKey = keyof EngineeringCounts;

export type EngineeringSort = "PRIORITY" | "OLDEST" | "NEWEST";

export interface EngineeringQueueFilters {
  documentType?: TechnicalDocumentType | "ALL";
  client?: string;
  project?: string;
  unitArea?: string;
  tag?: string;
  osNumber?: string;
  subOsNumber?: string;
  inspector?: string;
  plhProfessionalId?: string;
  status?: EngineeringWorkflowStatus | "ALL";
  from?: string;
  to?: string;
  priority?: TechnicalCorrectionPriority | "ALL";
  search?: string;
  card?: EngineeringCardKey | null;
  sort?: EngineeringSort;
}

export interface EngineeringMinutaRow {
  minuta: Minuta;
  workflowStatus: EngineeringWorkflowStatus;
  workflowLabel: string;
  waitingLabel: string;
  route: string;
}

export interface EngineeringBoard {
  rows: EngineeringQueueRow[];
  all: EngineeringQueueRow[];
  counts: EngineeringCounts;
  mine: MyPendencies;
  holds: DocumentHold[];
  corrections: TechnicalCorrectionRequest[];
  minutas: EngineeringMinutaRow[];
  /**
   * ATIVIDADE RECENTE — eventos REAIS do histórico das revisões e dos
   * pedidos de correção. Vazia quando ainda não houve movimento: a tela diz
   * isso, em vez de mostrar exemplos.
   */
  activity: EngineeringActivityEntry[];
  /** Contagens de correção pelos estados que existem no domínio. */
  correctionSummary: CorrectionSummary;
  /** Opções dos filtros, extraídas do que existe na fila. */
  options: { clients: string[]; projects: string[]; unitAreas: string[]; tags: string[]; inspectors: string[]; plhs: Array<{ id: string; name: string }>; osNumbers: string[]; subOsNumbers: string[] };
}

const uniq = (values: Array<string | null | undefined>) => Array.from(new Set(values.filter((v): v is string => !!v && v.trim() !== ""))).sort((a, b) => a.localeCompare(b, "pt-BR"));

export class EngineeringDashboardService {
  constructor(
    private readonly review: TechnicalReviewService,
    private readonly corrections: TechnicalCorrectionService,
    private readonly holds: DocumentHoldService,
    private readonly minutaService?: MinutaService
  ) {}

  private get auth() { return new RbacAuthorizationService(); }

  async loadBoard(context: RequestContext, filters: EngineeringQueueFilters = {}, now: Date = new Date()): Promise<EngineeringBoard> {
    this.auth.require(context, PermissionCode.EngineeringDashboardView);
    const canSeeQueue = context.permissions.has(PermissionCode.TechnicalReviewViewQueue);
    const [queue, pendingByDoc, allCorrections, activeHolds, minutas, reviewsWithHistory, everyCorrection] = await Promise.all([
      canSeeQueue ? this.review.listQueue(context) : Promise.resolve([] as TechnicalReviewQueueRow[]),
      this.corrections.pendingPriorityByDocument(context),
      this.corrections.listPending(context),
      context.permissions.has(PermissionCode.EngineeringHoldView) ? this.holds.listHolds(context, { status: "EM_HOLD" }) : Promise.resolve([] as DocumentHold[]),
      this.listMinutas(context),
      // Histórico completo das revisões — fonte da ATIVIDADE RECENTE. Sem a
      // permissão de fila não há atividade a mostrar, e não há exceção a
      // engolir: simplesmente não é consultado.
      canSeeQueue ? this.review.listReviewsWithHistory(context) : Promise.resolve([] as TechnicalDocumentReview[]),
      // TODOS os itens de correção, não só os pendentes: o resumo precisa
      // dos corrigidos e dos cancelados para contar cada estado real.
      this.corrections.listAll(context)
    ]);
    const holdByDoc = new Map(activeHolds.map(h => [`${h.documentType}:${h.documentId}`, h]));
    const me = context.professionalId ? String(context.professionalId) : null;

    const all: EngineeringQueueRow[] = queue.map(r => {
      const key = `${r.documentType}:${r.documentId}`;
      const pending = pendingByDoc.get(key) || { priority: null, count: 0 };
      const hold = holdByDoc.get(key) || null;
      const workflowStatus = hold ? "EM_HOLD" : (r.workflowStatus ?? workflowStatusOfReview({ status: r.status, cycle: r.cycle, correctedAt: r.correctedAt, signedAt: r.signedAt, finalizedAt: r.finalizedAt }));
      const submitted = new Date(r.submittedAt || 0).getTime();
      const pendingForPlh = r.status === "AWAITING_PLH_REVIEW" || r.status === "PLH_REVIEWING";
      return {
        ...r,
        workflowStatus,
        workflowLabel: ENGINEERING_WORKFLOW_STATUS_LABELS[workflowStatus],
        priority: pending.priority,
        pendingCorrections: pending.count,
        waitingLabel: relativeTimeLabel(r.submittedAt, now),
        waitingMs: Number.isFinite(submitted) ? Math.max(0, now.getTime() - submitted) : 0,
        onHold: !!hold,
        holdId: hold ? hold.id : null,
        mine: !!me && !!r.plhProfessionalId && String(r.plhProfessionalId) === me && pendingForPlh
      };
    });

    const minutaRows: EngineeringMinutaRow[] = minutas.map(m => {
      const ws = workflowStatusOfMinuta(m.status);
      return { minuta: m, workflowStatus: ws, workflowLabel: ENGINEERING_WORKFLOW_STATUS_LABELS[ws], waitingLabel: relativeTimeLabel(m.updatedAt ? new Date(m.updatedAt).toISOString() : null, now), route: `/integrity/recommendations-draft?minuta=${encodeURIComponent(m.id)}` };
    });

    const counts = this.countsOf(all, activeHolds, minutaRows);
    const mine: MyPendencies = {
      awaitingMyAnalysis: all.filter(r => r.mine && r.workflowStatus !== "REENVIADO_AO_PLH").length,
      resubmittedToMe: all.filter(r => r.mine && r.workflowStatus === "REENVIADO_AO_PLH").length,
      onHold: activeHolds.length,
      minutasPending: minutaRows.filter(r => ["AGUARDANDO_ANALISE_PLH", "EM_ANALISE_PLH", "CORRECAO_EM_ANDAMENTO"].includes(r.workflowStatus)).length,
      correctionsRequestedByMe: allCorrections.filter(c => context.userId && c.requestedBy.userId === context.userId).length
    };

    const rows = this.sort(this.filter(all, filters), filters.sort || "PRIORITY");
    return {
      rows,
      all,
      counts,
      mine,
      holds: activeHolds,
      corrections: allCorrections,
      minutas: minutaRows,
      activity: mergeEngineeringActivity(
        reviewsWithHistory.map(review => ({
          documentNumber: review.summary.documentNumber,
          tag: review.summary.tag,
          route: review.summary.route,
          history: review.history
        })),
        everyCorrection,
        { limit: 8 }
      ),
      correctionSummary: summarizeCorrections(everyCorrection, counts.resubmitted),
      options: {
        clients: uniq(all.map(r => r.client)),
        projects: uniq(all.map(r => r.project)),
        unitAreas: uniq(all.map(r => r.unitArea)),
        tags: uniq(all.map(r => r.tag)),
        inspectors: uniq(all.map(r => r.inspectorName)),
        plhs: Array.from(new Map(all.filter(r => r.plhProfessionalId).map(r => [r.plhProfessionalId!, r.plhName])).entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
        osNumbers: uniq(all.map(r => r.osNumber)),
        subOsNumbers: uniq(all.map(r => r.subOsNumber))
      }
    };
  }

  /** Cada card é um filtro: a mesma regra de contagem decide o que a fila mostra ao clicar. */
  matchesCard(row: EngineeringQueueRow, card: EngineeringCardKey): boolean {
    switch (card) {
      case "awaitingAnalysis": return row.workflowStatus === "AGUARDANDO_ANALISE_PLH";
      case "inAnalysis": return row.workflowStatus === "EM_ANALISE_PLH";
      case "avrForAnalysis": return row.documentType === "AVR";
      case "pendingCorrections": return row.pendingCorrections > 0;
      case "awaitingCorrection": return row.workflowStatus === "CORRECAO_SOLICITADA" || row.workflowStatus === "CORRECAO_EM_ANDAMENTO" || row.workflowStatus === "REPROVADO";
      case "resubmitted": return row.workflowStatus === "REENVIADO_AO_PLH";
      case "onHold": return row.workflowStatus === "EM_HOLD";
      case "minutaPending": return row.workflowStatus === "MINUTA_PENDENTE";
      case "approved": return row.workflowStatus === "APROVADO_PELO_PLH" || row.workflowStatus === "ASSINADO" || row.workflowStatus === "AGUARDANDO_FINALIZACAO" || row.workflowStatus === "FINALIZADO" || row.workflowStatus === "FINALIZADO_AGUARDANDO_ASSINATURA";
      case "awaitingApproval": return row.documentType === "INTEGRITY" && row.workflowStatus === "AGUARDANDO_FINALIZACAO" && !row.signedAt;
      case "awaitingFinalization": return !row.finalizedAt && (row.workflowStatus === "APROVADO_PELO_PLH" || row.workflowStatus === "ASSINADO" || row.workflowStatus === "AGUARDANDO_FINALIZACAO");
      case "finalized": return !!row.finalizedAt || row.workflowStatus === "FINALIZADO" || row.workflowStatus === "FINALIZADO_AGUARDANDO_ASSINATURA";
      case "minutasInProgress": return false;
    }
  }

  countsOf(all: EngineeringQueueRow[], holds: DocumentHold[], minutas: EngineeringMinutaRow[]): EngineeringCounts {
    return {
      awaitingAnalysis: all.filter(r => this.matchesCard(r, "awaitingAnalysis")).length,
      inAnalysis: all.filter(r => this.matchesCard(r, "inAnalysis")).length,
      avrForAnalysis: all.filter(r => this.matchesCard(r, "avrForAnalysis")).length,
      pendingCorrections: all.reduce((n, r) => n + r.pendingCorrections, 0),
      awaitingCorrection: all.filter(r => this.matchesCard(r, "awaitingCorrection")).length,
      resubmitted: all.filter(r => this.matchesCard(r, "resubmitted")).length,
      onHold: holds.length,
      minutaPending: all.filter(r => this.matchesCard(r, "minutaPending")).length,
      awaitingFinalization: all.filter(r => this.matchesCard(r, "awaitingFinalization")).length,
      finalized: all.filter(r => this.matchesCard(r, "finalized")).length,
      minutasInProgress: minutas.filter(m => ["EM_ELABORACAO", "AGUARDANDO_ANALISE_PLH", "EM_ANALISE_PLH", "CORRECAO_SOLICITADA", "CORRECAO_EM_ANDAMENTO"].includes(m.workflowStatus)).length,
      awaitingApproval: minutas.filter(m => m.workflowStatus === "MINUTA_VALIDADA").length + all.filter(r => this.matchesCard(r, "awaitingApproval")).length,
      approved: all.filter(r => this.matchesCard(r, "approved")).length
    };
  }

  filter(rows: EngineeringQueueRow[], f: EngineeringQueueFilters): EngineeringQueueRow[] {
    if (!Array.isArray(rows)) {
      throw new AppError(ErrorCode.Validation, "O parâmetro rows deve ser uma lista válida.");
    }
    const q = (f.search || "").trim().toLowerCase();
    const eq = (a: string | null | undefined, b: string | undefined) => !b || (a || "").toLowerCase() === b.toLowerCase();
    return rows
      .filter(r => !f.card || this.matchesCard(r, f.card))
      .filter(r => !f.documentType || f.documentType === "ALL" || r.documentType === f.documentType)
      .filter(r => eq(r.client, f.client))
      .filter(r => eq(r.project, f.project))
      .filter(r => eq(r.unitArea, f.unitArea))
      .filter(r => !f.tag || (r.tag || "").toLowerCase().includes(f.tag.toLowerCase()))
      .filter(r => eq(r.osNumber, f.osNumber))
      .filter(r => eq(r.subOsNumber, f.subOsNumber))
      .filter(r => eq(r.inspectorName, f.inspector))
      .filter(r => !f.plhProfessionalId || r.plhProfessionalId === f.plhProfessionalId)
      .filter(r => !f.status || f.status === "ALL" || r.workflowStatus === f.status)
      .filter(r => !f.from || (r.submittedAt || "").slice(0, 10) >= f.from)
      .filter(r => !f.to || (r.submittedAt || "").slice(0, 10) <= f.to)
      .filter(r => !f.priority || f.priority === "ALL" || r.priority === f.priority)
      .filter(r => !q || [r.documentNumber, r.tag, r.osNumber, r.subOsNumber, r.client, r.project, r.unitArea, r.elaboratedBy, r.inspectorName, r.plhName, TECHNICAL_DOCUMENT_TYPE_LABELS[r.documentType]].some(v => (v || "").toLowerCase().includes(q)));
  }

  /** PRIORITY: crítica primeiro, depois os mais antigos. OLDEST/NEWEST: só pela data de envio. */
  sort(rows: EngineeringQueueRow[], sort: EngineeringSort): EngineeringQueueRow[] {
    if (!Array.isArray(rows)) {
      throw new AppError(ErrorCode.Validation, "O parâmetro rows deve ser uma lista válida.");
    }
    const byOldest = (a: EngineeringQueueRow, b: EngineeringQueueRow) => (a.submittedAt || "").localeCompare(b.submittedAt || "");
    if (sort === "OLDEST") return [...rows].sort(byOldest);
    if (sort === "NEWEST") return [...rows].sort((a, b) => byOldest(b, a));
    return [...rows].sort((a, b) => priorityWeight(b.priority) - priorityWeight(a.priority) || byOldest(a, b));
  }

  private async listMinutas(context: RequestContext): Promise<Minuta[]> {
    if (!this.minutaService?.minutaRepo.listAll) return [];
    try {
      return [...await this.minutaService.minutaRepo.listAll(context)];
    } catch {
      return [];
    }
  }
}
